<script setup> 
    import { computed } from 'vue';
    import AnswerRates from '@/components/result/ai-literacy/AnswerRates.vue';
    import AnswerRateDistribution from '@/components/result/ai-literacy/AnswerRateDistribution.vue';
    
    const props = defineProps({
        teacherData: Object
    });
    
    import icon01 from '@/assets/images/result/ai/icon_01.svg';
    import icon02 from '@/assets/images/result/ai/icon_02.svg';
    import icon03 from '@/assets/images/result/ai/icon_03.svg';
    import icon04 from '@/assets/images/result/ai/icon_04.svg';

    // teacherData 기반 우리 반 정답률 (field1_rate, field2_rate, field3_rate, field4_rate)
    const ourClassPercentages = computed(() => {
        if (!props.teacherData) {
            return [85, 70, 20, 65];
        }
        return [
            parseFloat(props.teacherData.field1_rate) || 0, // AI·디지털 기술
            parseFloat(props.teacherData.field2_rate) || 0, // AI·디지털 활용  
            parseFloat(props.teacherData.field3_rate) || 0, // AI·디지털 윤리
            parseFloat(props.teacherData.field4_rate) || 0  // 인간 중심 AI
        ];
    });

    // teacherData 기반 학교 전체 정답률 (school_field1_rate, school_field2_rate, school_field3_rate, school_field4_rate)
    const overallPercentages = computed(() => {
        if (!props.teacherData) {
            return [75, 35, 80, 70];
        }
        return [
            parseFloat(props.teacherData.school_field1_rate) || 0, // AI·디지털 기술
            parseFloat(props.teacherData.school_field2_rate) || 0, // AI·디지털 활용
            parseFloat(props.teacherData.school_field3_rate) || 0, // AI·디지털 윤리  
            parseFloat(props.teacherData.school_field4_rate) || 0  // 인간 중심 AI
        ];
    });

    // teacherData 기반 세부 분야별 데이터
    const aiSubData = computed(() => {
        if (!props.teacherData) {
            return [
                { 
                    title: 'AI·디지털 기술', 
                    icon: icon01,
                    items: [
                        { label: '인공지능 기초', value: 81.5, color: '#0F7EF1' },
                        { label: '기계학습과 딥러닝', value: 82.0, color: '#FF889E' },
                        { label: '생성형 인공지능', value: 50, color: '#9BD380' }
                    ]
                },
                { 
                    title: 'AI·디지털 활용', 
                    icon: icon02,
                    items: [
                        { label: '문제 해결', value: 81.5, color: '#0F7EF1' },
                        { label: '콘텐츠 창작', value: 82.0, color: '#FF889E' },
                        { label: '학습 및 탐구', value: 85.2, color: '#9BD380' }
                    ]
                },
                { 
                    title: 'AI·디지털 윤리', 
                    icon: icon03,
                    items: [
                        { label: '주체적인 사용', value: 69.5, color: '#0F7EF1' },
                        { label: '책임감 있는 사용', value: 82.0, color: '#FF889E' },
                        { label: '사회적 영향', value: 75.2, color: '#9BD380' }
                    ]
                },
                { 
                    title: '인간 중심 AI',
                    icon: icon04, 
                    items: [
                        { label: '인간중심 AI 설계', value: 77.0, color: '#0F7EF1' },
                        { label: '인간과 AI의 협업', value: 80.0, color: '#9BD380' }
                    ]
                }
            ];
        }
        //초등일때
        if(props.teacherData.schul_type === 'E'){
            return [
                
                { 
                    title: 'AI·디지털 기술', 
                    icon: icon01,
                    items: [
                        { label: '인공지능 기초', value: parseFloat(props.teacherData.field1_1_rate) || 0, color: '#0F7EF1' }, // field1_1_rate
                        { label: '기계학습과 딥러닝', value: parseFloat(props.teacherData.field1_2_rate) || 0, color: '#FF889E' }, // field1_2_rate
                        { label: '생성형 인공지능', value: parseFloat(props.teacherData.field1_3_rate) || 0, color: '#9BD380' } // field1_3_rate
                    ]
                },
                { 
                    title: 'AI·디지털 활용', 
                    icon: icon02,
                    items: [
                        { label: '문제해결', value: parseFloat(props.teacherData.field2_1_rate) || 0, color: '#0F7EF1' }, // field2_1_rate
                        { label: '콘텐츠 창작', value: parseFloat(props.teacherData.field2_2_rate) || 0, color: '#FF889E' }, // field2_2_rate
                        { label: '학습 및 탐구', value: parseFloat(props.teacherData.field2_3_rate) || 0, color: '#9BD380' } // field2_3_rate
                    ]
                },
                { 
                    title: 'AI·디지털 윤리', 
                    icon: icon03,
                    items: [
                        { label: '사회적 영향', value: parseFloat(props.teacherData.field3_1_rate) || 0, color: '#0F7EF1' }, // field3_1_rate
                        { label: '주체성과 안전한 사용', value: parseFloat(props.teacherData.field3_2_rate) || 0, color: '#FF889E' }, // field3_2_rate
                        { label: '책임감 있는 사용', value: parseFloat(props.teacherData.field3_3_rate) || 0, color: '#9BD380' } // field3_3_rate
                    ]
                },
                { 
                    title: '인간 중심 AI',
                    icon: icon04, 
                    items: [
                        { label: '인간중심 AI 설계', value: parseFloat(props.teacherData.field4_1_rate) || 0, color: '#0F7EF1' }, // field4_1_rate
                        { label: 'AI생애주기', value: parseFloat(props.teacherData.field4_2_rate) || 0, color: '#9BD380' } // field4_2_rate
                    ]
                }
            ];

        //중등일때
        }else if(props.teacherData.schul_type === 'M'){

            return [
                
                { 
                    title: 'AI·디지털 기술', 
                    icon: icon01,
                    items: [
                        { label: '인공지능 기초', value: parseFloat(props.teacherData.field1_1_rate) || 0, color: '#0F7EF1' }, // field1_1_rate
                        { label: '기계학습과 딥러닝', value: parseFloat(props.teacherData.field1_2_rate) || 0, color: '#FF889E' }, // field1_2_rate
                        { label: '생성형 인공지능', value: parseFloat(props.teacherData.field1_3_rate) || 0, color: '#9BD380' } // field1_3_rate
                    ]
                },
                { 
                    title: 'AI·디지털 활용', 
                    icon: icon02,
                    items: [
                        { label: '문제 해결', value: parseFloat(props.teacherData.field2_1_rate) || 0, color: '#0F7EF1' }, // field2_1_rate
                        { label: '콘텐츠 창작', value: parseFloat(props.teacherData.field2_2_rate) || 0, color: '#FF889E' }, // field2_2_rate
                        { label: '학습 및 탐구', value: parseFloat(props.teacherData.field2_3_rate) || 0, color: '#9BD380' } // field2_3_rate
                    ]
                },
                { 
                    title: 'AI·디지털 윤리', 
                    icon: icon03,
                    items: [
                        { label: '사회적 영향', value: parseFloat(props.teacherData.field3_1_rate) || 0, color: '#0F7EF1' }, // field3_1_rate
                        { label: '주체성과 안전한 사용', value: parseFloat(props.teacherData.field3_2_rate) || 0, color: '#FF889E' }, // field3_2_rate
                        { label: '책임감 있는 사용', value: parseFloat(props.teacherData.field3_3_rate) || 0, color: '#9BD380' } // field3_3_rate
                    ]
                },
                { 
                    title: '인간 중심 AI',
                    icon: icon04, 
                    items: [
                        { label: '인간중심 AI 설계', value: parseFloat(props.teacherData.field4_1_rate) || 0, color: '#0F7EF1' }, // field4_1_rate
                        { label: 'AI생애주기', value: parseFloat(props.teacherData.field4_2_rate) || 0, color: '#9BD380' } // field4_2_rate
                    ]
                }
            ];
        
        }
    
    });

    const userRole = 'teacher';  
</script>
<template>
    <div class="report-inner">   
        <div class="card-wrap">
            <AnswerRates                
                :our-class-percentages="overallPercentages"
                :overall-percentages="ourClassPercentages"
                :isStudent="true"
                :userRole="userRole"
            />         
            <AnswerRateDistribution :sub-data="aiSubData" />
        </div>
    </div>
</template>